// PURE descriptor -> Mode[] mapping. No audio, no DOM, no I/O.
// Deterministic for a fixed (descriptor, pitchHz, strikePos, strikeCounter).

import type { BodyClass, DiatomDescriptor, Mode, StrikePos } from '@shared/index';
import { mulberry32 } from '@shared/index';
import { RATIO_SETS, SONIC_BASES, stretchedString } from './ratios';

export interface GeometryOpts {
  /** Sample rate (Hz). Default 48000. */
  fs: number;
  /** Hard cap on returned modes. Default 24. */
  maxModes: number;
  /** Optional strike position in body frame; absent => uniform radial weighting. */
  strikePos?: StrikePos;
  /** Per-strike index; mixes into the micro-detune RNG seed. Default 0. */
  strikeCounter?: number;
  /** Override the body-class spectrum with a named sonic base (gamelan/glass/sub/organ/fm). */
  ratioFamily?: string | undefined;
}

const DEFAULT_FS = 48000;
const DEFAULT_MAX_MODES = 24;
const NYQUIST_FACTOR = 0.485;
const GAIN_PRUNE_FLOOR = 0.015;
const RADIAL_FLOOR = 0.02;
const DEDUPE_HZ = 0.5;
const T60_MIN = 0.04;
const T60_MAX = 12;

const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;
const clamp = (x: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, x));

function isPlateOrBell(bodyClass: BodyClass): boolean {
  return bodyClass === 'circular-plate' || bodyClass === 'bell';
}

/** One mode mid-pipeline, before flattening to a Mode. */
interface WorkMode {
  m: number; // 1-indexed mode number (ratio index)
  ratio: number;
  freq: number; // pitch * ratio * detune (pre-Nyquist)
  emphasis: number;
  indexRolloff: number;
  angular: number;
  radial: number;
  isFundamental: boolean;
}

/**
 * Map a diatom descriptor + target pitch to a deterministic, individualized mode list.
 *
 * Pipeline: pick/generate ratios -> per-mode detune (ind then strike, in mode-index
 * order) -> degeneracy splits (N>=2 plate/bell) -> strike-position weighting -> freq,
 * Nyquist drop -> t60 (pore-scaled, decay scatter, bell hum boost) -> amp shaping ->
 * sort, dedupe (<0.5 Hz), prune (amp<0.015), cap to maxModes by top amp, normalize peak=1.
 */
export function geometryToModes(
  descriptor: DiatomDescriptor,
  pitchHz: number,
  opts?: Partial<GeometryOpts>,
): Mode[] {
  const fs = opts?.fs ?? DEFAULT_FS;
  const maxModes = opts?.maxModes ?? DEFAULT_MAX_MODES;
  const strikeCounter = opts?.strikeCounter ?? 0;
  const strikePos = opts?.strikePos;

  const { bodyClass, symmetryN: N, poreDensity, defect, seed } = descriptor;

  // 1) Base ratio array. A preset-selected sonic base overrides the body-class spectrum.
  let ratios: readonly number[];
  const family = opts?.ratioFamily;
  if (family && SONIC_BASES[family]) {
    ratios = SONIC_BASES[family];
  } else if (bodyClass === 'stretched-string') {
    const B = lerp(0.0001, 0.001, poreDensity);
    const generated: number[] = [];
    for (let n = 1; n <= 14; n++) generated.push(stretchedString(n, B));
    ratios = generated;
  } else {
    ratios = RATIO_SETS[bodyClass];
  }

  // 2) PRNGs. Per-individual stream from seed; per-strike stream from mixed seed.
  const ind = mulberry32(seed >>> 0);
  const strikeSeed = (seed ^ 0x9e3779b9 ^ Math.imul(strikeCounter, 2654435761)) >>> 0;
  const str = mulberry32(strikeSeed);

  const detuneSpread = 0.0015 + 0.012 * defect + 0.004 * poreDensity;

  const splittable = isPlateOrBell(bodyClass) && N >= 2;

  // 3) Per-mode detune draws FIRST, in strict mode-index order (determinism-critical).
  //    For each ratio: draw ind() then str() (fixed order).
  const work: WorkMode[] = [];
  for (let i = 0; i < ratios.length; i++) {
    const m = i + 1;
    const ratio = ratios[i];

    const indDetune = (ind() * 2 - 1) * detuneSpread;
    const strikeDetune = (str() * 2 - 1) * 0.005;
    const detune = (1 + indDetune) * (1 + strikeDetune);
    const freq = pitchHz * ratio * detune;

    const isFundamental = m === 1;
    let emphasis = 0.55;
    let angular = 1;
    if (isPlateOrBell(bodyClass)) {
      const onMultiple = m % N === 0 || isFundamental;
      emphasis = onMultiple ? 1.0 : 0.55;
    }
    // index rolloff so low modes lead
    const indexRolloff = 1 / (1 + 0.12 * (m - 1));

    // strike-position weighting
    let radial = 1;
    if (strikePos) {
      const { r, theta } = strikePos;
      const radialProfile =
        bodyClass === 'circular-plate' || bodyClass === 'bell'
          ? Math.abs(Math.sin(Math.PI * m * r * 0.5))
          : Math.abs(Math.sin(Math.PI * m * r));
      angular = 0.4 + 0.6 * Math.abs(Math.cos(Math.min(m, N) * theta));
      radial = clamp(radialProfile * angular, RADIAL_FLOOR, 1);
    } else {
      radial = 1;
    }

    work.push({ m, ratio, freq, emphasis, indexRolloff, angular, radial, isFundamental });
  }

  // 4) Degeneracy splits (N>=2 plate/bell): emphasized modes (except m==1) split into a
  //    close pair freq*(1 +/- delta/2), each carrying half the amplitude.
  const delta = 0.004 + 0.02 * defect;
  const split: WorkMode[] = [];
  for (const w of work) {
    const isEmphasized = w.emphasis >= 1.0 && !w.isFundamental;
    if (splittable && isEmphasized) {
      split.push({ ...w, freq: w.freq * (1 - delta / 2), emphasis: w.emphasis * 0.5 });
      split.push({ ...w, freq: w.freq * (1 + delta / 2), emphasis: w.emphasis * 0.5 });
    } else {
      split.push(w);
    }
  }

  // 5) t60 model.
  const T60_0 = lerp(4.5, 0.9, poreDensity);
  const p = lerp(0.85, 1.45, poreDensity);
  const decaySpread = 0.04 + 0.1 * defect;

  // 6) Build modes: Nyquist drop, t60, amp.
  const nyquist = NYQUIST_FACTOR * fs;
  type Built = { freq: number; t60: number; amp: number };
  const built: Built[] = [];
  for (const w of split) {
    if (w.freq >= nyquist) continue;
    if (w.freq <= 0) continue;

    // decay scatter draws from the per-individual stream after all detune draws.
    let t60 = T60_0 * Math.pow(pitchHz / w.freq, p);
    if (bodyClass === 'bell' && w.isFundamental) t60 *= 1.25;
    t60 *= 1 + (ind() * 2 - 1) * decaySpread;
    t60 = clamp(t60, T60_MIN, T60_MAX);

    const loudnessShape = 1 / Math.sqrt(w.m);
    const amp = w.angular * w.radial * loudnessShape * w.emphasis * w.indexRolloff;

    built.push({ freq: w.freq, t60, amp });
  }

  // 7) Sort by freq asc.
  built.sort((a, b) => a.freq - b.freq);

  // 8) Dedupe near-equal freqs (<0.5 Hz apart) keeping the higher amp.
  const deduped: Built[] = [];
  for (const b of built) {
    const last = deduped[deduped.length - 1];
    if (last && b.freq - last.freq < DEDUPE_HZ) {
      if (b.amp > last.amp) deduped[deduped.length - 1] = b;
    } else {
      deduped.push(b);
    }
  }

  // 9) Prune amp < floor (before the cap so budget goes to audible modes).
  let kept = deduped.filter((b) => b.amp >= GAIN_PRUNE_FLOOR);

  // Guarantee at least one mode survives (use the loudest if pruning emptied it).
  if (kept.length === 0 && deduped.length > 0) {
    const loudest = deduped.reduce((acc, b) => (b.amp > acc.amp ? b : acc), deduped[0]);
    kept = [loudest];
  }

  // 10) Cap to maxModes by top amplitude, then re-sort by freq. `kept` is a locally-owned array
  // (from filter / single-element), so sort + truncate it in place — no defensive copy needed.
  if (kept.length > maxModes) {
    kept.sort((a, b) => b.amp - a.amp);
    kept.length = maxModes;
    kept.sort((a, b) => a.freq - b.freq);
  }

  // 11) Normalize so the loudest mode amp == 1.0.
  let maxAmp = 0;
  for (const b of kept) if (b.amp > maxAmp) maxAmp = b.amp;
  const norm = maxAmp > 0 ? 1 / maxAmp : 1;

  return kept.map((b) => ({ freq: b.freq, t60: b.t60, amp: b.amp * norm }));
}
